import * as Dialog from '@radix-ui/react-dialog';
import { animated, config, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import { X } from 'phosphor-react';
import type { PropsWithChildren } from 'react';
import { useCallback } from 'react';
import { ModalBackground } from './ds/modal/modal-background';
import { IconButton } from './icon-button';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
};

export const ModalLegacy = ({ children, isOpen, onClose, className }: PropsWithChildren<ModalProps>) => {
  const modalTransitions = useTransition(isOpen, {
    from: { opacity: 0, y: -20 },
    enter: { opacity: 1, y: 0 },
    leave: { opacity: 0, y: -20 },
    config: config.stiff,
  });

  const onOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  }, [onClose]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <ModalBackground isOpen={isOpen} />

        {modalTransitions((transitionStyle, item) => item ? (
          /*
            Styling Dialog.Content to be fullscreen prevents closing the modal when clicking outside the actual
            (visible) modal. Wrapping Dialog.Content in a fullscreen div does not work as the Dialog.Content will be
            rendered in radix-portal outside of the application's DOM, the wrapping div however will be inside of the
            application's DOM.
          */
          <Dialog.Content className="fixed inset-0 z-50 overflow-y-auto" aria-describedby={undefined}>
            <div className="flex items-center justify-center min-h-screen">
              <animated.div style={transitionStyle} className={classNames('relative mx-auto rounded-lg bg-contrast m-8 shadow-lg', className)}>
                {children}
                <CloseButton />
              </animated.div>
            </div>
          </Dialog.Content>
        ) : null)}
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const CloseButton = () => (
  <Dialog.Close asChild={true}>
    <div className="absolute top-2 right-2">
      <IconButton icon={X} iconOnly={true} variant="light" label="Schließen" />
    </div>
  </Dialog.Close>
);

export default ModalLegacy;
